import type { TimelineLegendEntry } from '@/lib/timeline-option'
import { cn } from '@/lib/utils'

/**
 * The timeline's series toggles, as one row of HTML buttons above the chart
 * that scrolls sideways (swipe on touch, shift+wheel / trackpad on desktop)
 * when the series don't fit -- ECharts' own canvas legend can only page
 * through them with arrow buttons. Also keyboard operable, unlike the
 * canvas legend. Which series are shown still lives in the chart's legend
 * component; `onToggle` drives it.
 */
export function TimelineLegend({
  entries,
  hidden,
  onToggle,
}: {
  entries: TimelineLegendEntry[]
  /** Names of the series currently toggled off. */
  hidden: ReadonlySet<string>
  onToggle: (name: string) => void
}) {
  return (
    <div
      role="group"
      aria-label="Chart series"
      className="flex min-w-0 shrink-0 items-center gap-1 overflow-x-auto overscroll-x-contain"
    >
      {entries.map((entry) => {
        const on = !hidden.has(entry.name)
        return (
          <button
            key={entry.name}
            type="button"
            aria-pressed={on}
            title={on ? `Hide ${entry.name}` : `Show ${entry.name}`}
            onClick={() => onToggle(entry.name)}
            className={cn(
              'hover:bg-accent focus-visible:ring-ring inline-flex shrink-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-xs whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-inset',
              on ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            <span
              aria-hidden="true"
              className={cn('w-4 border-t-2', !on && 'border-muted-foreground opacity-60')}
              style={{
                borderTopStyle: entry.dashed ? 'dashed' : 'solid',
                ...(on ? { borderTopColor: entry.color } : {}),
              }}
            />
            {entry.name}
          </button>
        )
      })}
    </div>
  )
}
